import { FF_IMMEDIATE_D2D_CONSULTATION } from '@/constants/featureFlags';
import { GoogleAnalyticsEventName } from '@/constants/types';
import { consultEligibleDoctorSteps } from '@/data/ConsultEligibleDoctor/consult-eligible-doctor-steps';
import { useD2DTracking } from '@/hooks/useD2DTracking';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import useGoogleTagManager from '@/hooks/useGoogleTagManager';
import { useInstantConsultRequest } from '@/hooks/useInstantConsultRequest';
import { usePatientOnboardingDetails } from '@/hooks/usePatientOnboardingDetails';
import { WithPageWrapper } from '@/pages/components/Branding';
import { formatStringToSha256Hash } from '@/utils/ga-hashing';
import { getSearchParams } from '@/utils/url';
import { Alert, CircularProgress } from '@montugroup/design-system';
import { FormBuilder, useFormBuilder } from '@montugroup/form-builder';
import { Logger } from '@montugroup/utilities';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { useEffect } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { ButtonsContainer, Paragraph, StyledButton } from './ConsultEligibleDoctor.styles';

const logger = new Logger('ConsultEligibleDoctor');

export const ConsultEligibleDoctorParagraph = () => {
  const { flags } = useFeatureFlags();
  const isImmediateDoctorConsultEnabled = flags[FF_IMMEDIATE_D2D_CONSULTATION];

  if (!isImmediateDoctorConsultEnabled) {
    return <Paragraph>Schedule a consult at a time that suits you below.</Paragraph>;
  }

  return <Paragraph>Choose to speak to a doctor now or schedule a consult at a time that suits you below.</Paragraph>;
};

export const ConsultEligibleDoctor = () => {
  const { search } = useLocation();
  const { setCurrentStepIndex } = useFormBuilder();
  const { patientOnboardingDetails } = usePatientOnboardingDetails();
  const { sendInstantConsultRequest, isPending, isError } = useInstantConsultRequest();
  const { flags, loading: flagsLoading } = useFeatureFlags();
  const isImmediateDoctorConsultEnabled = flags[FF_IMMEDIATE_D2D_CONSULTATION];

  const { sendGoogleAnalyticsEvent } = useGoogleTagManager();
  const { trackImmediateD2DOffered, trackStartBooking } = useD2DTracking();
  const userEmail = getSearchParams('email', { localStorageKey: 'prescreeningUserEmail' });
  const promocode = getSearchParams('promocode');
  const shaEmail = userEmail ? formatStringToSha256Hash(userEmail) : '';

  useEffect(() => {
    // Distinguishes a closed doctor line from patients choosing to schedule
    if (flagsLoading) {
      return;
    }

    trackImmediateD2DOffered(Boolean(isImmediateDoctorConsultEnabled));
  }, [trackImmediateD2DOffered, flagsLoading, isImmediateDoctorConsultEnabled]);

  const handleSpeakToDoctorNow = async () => {
    trackStartBooking('Immediate D2D Consultation');

    try {
      const { firstName, lastName, phoneNumber, email } = patientOnboardingDetails!;

      await sendInstantConsultRequest({
        variant: 'variant-amazon-connect-initiated-inc',
        attributes: { firstName, lastName, phone: phoneNumber, email, callType: 'VIDEO', intakeVariant: 'D2D' },
      });

      sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.SUBMIT_BOOKING, {
        event_category: 'cal.com',
        event_label: 'Immediate D2D Consultation',
        sha_email: shaEmail,
        sha_gads_phone_number: formatStringToSha256Hash(phoneNumber),
        sha_fb_phone_number: formatStringToSha256Hash(phoneNumber),
        sha_first_name: formatStringToSha256Hash(firstName),
        sha_last_name: formatStringToSha256Hash(lastName),
        ...(promocode ? { promocode } : {}),
      });

      setCurrentStepIndex((prev) => prev + 1);
    } catch (error) {
      logger.error('An error occurred while requesting an instant doctor consult: ', error);
    }
  };

  return (
    <ButtonsContainer>
      {isImmediateDoctorConsultEnabled && (
        <>
          {isError && (
            <Alert severity="error">An error has occurred. Please retry or schedule a consult instead.</Alert>
          )}
          <StyledButton
            fullWidth
            size="large"
            variant="contained"
            disabled={isPending}
            onClick={handleSpeakToDoctorNow}
            endIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Speak to a doctor now
          </StyledButton>
          <Alert severity="info" icon={<AccessTimeOutlinedIcon fontSize="small" />}>
            <strong>Average wait</strong> to see next available doctor approx. <strong>5-10 minutes</strong>
          </Alert>
        </>
      )}
      <StyledButton
        fullWidth
        size="large"
        variant="outlined"
        component={Link}
        to={`/consult-eligible-doctor/schedule${search}`}
        onClick={() => trackStartBooking('Initial D2D Consultation')}
      >
        Schedule a consult
      </StyledButton>
    </ButtonsContainer>
  );
};

export const ConsultEligibleDoctorForm = () => {
  const { patientOnboardingDetails } = usePatientOnboardingDetails();

  if (!patientOnboardingDetails) {
    return <Navigate to="/error" />;
  }

  return WithPageWrapper(
    <FormBuilder
      formId="consult-eligible-doctor-form"
      postUrl="TODO"
      steps={consultEligibleDoctorSteps()}
      stepButtonsConfig={{ fullWidthStepButtons: true }}
    />,
    true,
  );
};
